import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState } from '@playerbase/ui';
import type { DiscordConnection, DiscordGuildChannel } from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import { BackfillStatusBanner } from '../../shell/job-status-banners.js';
import { fetchDiscordConnectionForGame } from '../../games/discord-data.js';
import { useDiscordConnectionRealtime } from '../../games/use-discord-connection-realtime.js';
import {
  fetchStudioChannelWatchers,
  requestChannelBackfill,
  requestDiscordChannelsResync,
  updateGameWatchedChannels,
  type StudioChannelWatcher,
  type UpdateWatchedChannelsError,
} from '../../games/discord-channels-data.js';
import { useDiscordGuildChannels } from '../../games/use-discord-guild-channels.js';
import { AddChannelModal } from './watched-channels/add-channel-modal.js';
import { WatchedChannelsList } from './watched-channels/watched-channels-list.js';
import { RemoveChannelConfirmDialog } from './watched-channels/remove-channel-confirm-dialog.js';

// ---------------------------------------------------------------------------
// Watched channels sub-page. Lives at /g/:gameShortId/feedback-settings/watched-channels
// and is the top item on the feedback-settings nav. Studios pick which Discord
// channels feed the Feedback Pool here; the underlying server connection is
// managed separately under Game Settings > Discord connection.
//
// Owns its own Discord connection load so it can render an actionable empty
// state (with a deep link back to the connection page) when no usable
// connection exists yet. Once the connection is connected/pending we mount
// the channel picker + list.
// ---------------------------------------------------------------------------

function updateErrorMessage(code: UpdateWatchedChannelsError): string {
  switch (code) {
    case 'not_authenticated':
      return 'You need to be signed in. Try signing in again.';
    case 'not_a_member':
      return 'You are not a member of this studio.';
    case 'game_not_found':
      return 'This game no longer exists. Refresh the page.';
    case 'game_not_connected':
      return 'This game is not connected to a Discord server yet. Connect Discord first.';
    case 'connection_disconnected':
      return 'The Discord server has been disconnected. Reconnect it from Discord connection.';
    case 'unknown_channel_id':
      return 'One of the channels could not be verified - it may have been deleted in Discord. Refresh and try again.';
    case 'channel_ids_required':
      return 'Could not save: empty channel list. Try again.';
    case 'unknown':
    default:
      return 'Something went wrong saving the watched channels. Try again.';
  }
}

function channelLabel(channel: DiscordGuildChannel | null, channelId: string): string {
  if (channel === null) return `channel ${channelId}`;
  return channel.parentName !== null
    ? `${channel.parentName} / # ${channel.name}`
    : `# ${channel.name}`;
}

function hasUsableConnection(connection: DiscordConnection | null): boolean {
  if (connection === null) return false;
  return connection.status === 'connected' || connection.status === 'pending';
}

export function WatchedChannelsPage(): JSX.Element {
  const { activeStudio, activeGame } = useShell();
  const [connection, setConnection] = useState<DiscordConnection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(true);

  const loadConnection = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setConnection(null);
      setConnectionLoading(false);
      return;
    }
    const next = await fetchDiscordConnectionForGame(activeGame.id);
    setConnection(next);
    setConnectionLoading(false);
  }, [activeGame]);

  useEffect(() => {
    void loadConnection();
  }, [loadConnection]);

  useDiscordConnectionRealtime(connection?.id ?? null, () => {
    void loadConnection();
  });

  const discordConnectionHref =
    activeGame === null
      ? ''
      : `/s/${activeStudio.shortId}/g/${activeGame.shortId}/game-settings/discord`;

  if (activeGame === null) {
    return (
      <div className="flex min-h-full flex-1 flex-col">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
          <p className="text-sm text-text-muted">
            Select a game to manage its watched channels.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-text">
          Watched channels
        </h1>
        <p className="mt-2 text-sm text-text-muted">
          Messages posted in these channels feed the Feedback Pool. Use the
          history option when adding a channel to also pull recent messages from
          before it was watched.
        </p>
      </header>

      {connectionLoading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : !hasUsableConnection(connection) ? (
        <EmptyState
          eyebrow="Discord connection required"
          title="Connect Discord first"
          description="PlayerBase needs a connected Discord server before you can pick channels for the bot to watch."
          cta={
            <Button asChild variant="primary" size="md">
              <Link to={discordConnectionHref}>Go to Discord connection</Link>
            </Button>
          }
        />
      ) : (
        <WatchedChannelsManager
          gameId={activeGame.id}
          connection={connection as DiscordConnection}
          onConnectionChanged={loadConnection}
        />
      )}
      </div>
    </div>
  );
}

// Channels-picker chrome: list + add modal + remove dialog. Split out so the
// page-level connection state (no game / no connection) can stay declarative
// while the picker still owns its own data loading and modals.
type WatchedChannelsManagerProps = {
  gameId: string;
  connection: DiscordConnection;
  onConnectionChanged: () => Promise<void> | void;
};

function WatchedChannelsManager({
  gameId,
  connection,
  onConnectionChanged,
}: WatchedChannelsManagerProps): JSX.Element {
  // Per-game watched-channel data lives on the new game_watched_channels
  // table (migration 0082); the connection's legacy `watchedChannelIds`
  // is no longer authoritative. We load via get_studio_channel_watchers
  // which also gives us the "also watched by [Other Game]" hints in one
  // round trip.
  const [watchers, setWatchers] = useState<StudioChannelWatcher[]>([]);
  const reloadWatchers = useCallback(async () => {
    const next = await fetchStudioChannelWatchers(gameId);
    setWatchers(next);
  }, [gameId]);
  useEffect(() => {
    void reloadWatchers();
  }, [reloadWatchers]);

  const watchedIds = useMemo(
    () =>
      watchers
        .filter((w) => w.thisGameWatches)
        .map((w) => w.discordChannelId),
    [watchers],
  );
  const watchedSet = useMemo(() => new Set(watchedIds), [watchedIds]);
  const otherGameSlugsByChannelId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const w of watchers) {
      if (w.otherGameSlugs.length > 0) {
        map.set(w.discordChannelId, w.otherGameSlugs);
      }
    }
    return map;
  }, [watchers]);
  const { channels, loading, reload } = useDiscordGuildChannels(connection.id);

  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const channelsByChannelId = useMemo(() => {
    const map = new Map<string, DiscordGuildChannel>();
    for (const c of channels) map.set(c.channelId, c);
    return map;
  }, [channels]);

  const watchedRows = useMemo(
    () =>
      watchedIds.map((channelId) => ({
        channelId,
        channel: channelsByChannelId.get(channelId) ?? null,
      })),
    [watchedIds, channelsByChannelId],
  );

  const handleConfirmAdd = useCallback(
    async (selectedChannelIds: string[], backfillDays: number): Promise<void> => {
      setAdding(true);
      setAddError(null);
      try {
        const existing = new Set(watchedIds);
        // Channels the user is actually adding right now (not the
        // already-watched ones the modal would have disabled). The
        // backfill request only covers these so re-saving an unchanged
        // set never re-streams history.
        const newlyAdded = selectedChannelIds.filter((id) => !existing.has(id));

        const next = [...new Set([...watchedIds, ...selectedChannelIds])];
        const result = await updateGameWatchedChannels(gameId, next);
        if (!result.ok) {
          setAddError(updateErrorMessage(result.code));
          return;
        }

        // Fire-and-forget backfill - don't block the modal close on it.
        // The bot reports progress on the discord_backfill_job row, which the
        // shell surfaces as a toast + the inline "Importing history..." row
        // below, so the picker stays optimistic once the watched-channels
        // write has succeeded.
        if (backfillDays > 0 && newlyAdded.length > 0) {
          void requestChannelBackfill(connection.id, gameId, newlyAdded, backfillDays);
        }

        setShowAdd(false);
        await reloadWatchers();
        await onConnectionChanged();
      } finally {
        setAdding(false);
      }
    },
    [gameId, connection.id, watchedIds, onConnectionChanged, reloadWatchers],
  );

  const handleRefresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    try {
      await requestDiscordChannelsResync(connection.id);
      // The bot writes back into discord_guild_channels and Realtime pushes
      // the change in. Give it a moment, then refetch as a belt-and-braces.
      setTimeout(() => {
        void reload();
        setRefreshing(false);
      }, 800);
    } catch {
      setRefreshing(false);
    }
  }, [connection.id, reload]);

  const handleConfirmRemove = useCallback(async (): Promise<void> => {
    if (removeTarget === null) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const next = watchedIds.filter((id) => id !== removeTarget);
      const result = await updateGameWatchedChannels(gameId, next);
      if (!result.ok) {
        setRemoveError(updateErrorMessage(result.code));
        return;
      }
      setRemoveTarget(null);
      await reloadWatchers();
      await onConnectionChanged();
    } finally {
      setRemoving(false);
    }
  }, [gameId, removeTarget, watchedIds, onConnectionChanged, reloadWatchers]);

  return (
    <div className="flex flex-col gap-4">
      <BackfillStatusBanner />

      {watchedIds.length > 0 && (
        <div className="flex justify-end">
          <Button variant="primary" size="md" onClick={() => setShowAdd(true)}>
            Add channel
          </Button>
        </div>
      )}

      {loading && watchedIds.length > 0 ? (
        <p className="text-sm text-text-muted">Loading channels…</p>
      ) : watchedIds.length === 0 ? (
        <EmptyState
          eyebrow="Watched channels"
          title="Pick channels for the bot to watch"
          description="The bot will start ingesting messages from selected channels into the Feedback Pool. Optionally also pull recent history from each channel when you add it."
          cta={
            <Button variant="primary" size="md" onClick={() => setShowAdd(true)}>
              Add channel
            </Button>
          }
        />
      ) : (
        <WatchedChannelsList
          rows={watchedRows}
          onRemove={(channelId) => {
            setRemoveError(null);
            setRemoveTarget(channelId);
          }}
          busyChannelId={removing ? removeTarget : null}
        />
      )}

      <AddChannelModal
        open={showAdd}
        channels={channels}
        alreadyWatchedIds={watchedSet}
        otherGameSlugsByChannelId={otherGameSlugsByChannelId}
        busy={adding}
        refreshing={refreshing}
        errorMessage={addError}
        onConfirm={(ids, days) => void handleConfirmAdd(ids, days)}
        onCancel={() => {
          if (adding) return;
          setShowAdd(false);
          setAddError(null);
        }}
        onRefresh={() => void handleRefresh()}
      />

      <RemoveChannelConfirmDialog
        open={removeTarget !== null}
        busy={removing}
        errorMessage={removeError}
        channelLabel={
          removeTarget === null
            ? ''
            : channelLabel(channelsByChannelId.get(removeTarget) ?? null, removeTarget)
        }
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => {
          if (removing) return;
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />
    </div>
  );
}
